import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Search, Eye, Edit, X, AlertCircle, Building2, Plus, Power, PowerOff, Users, Link2, FileText, Receipt } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import {
  listAdminCompanies,
  updateAdminCompanyStatus,
  getStatusImpact,
  listPlans
} from "../../services/adminCompanies";

const Companies = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    planId: "",
    overdue: "",
    fiscalReady: ""
  });
  const [toast, setToast] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, company: null, reason: "", impact: null, loading: false });

  useEffect(() => {
    load(1);
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await listPlans();
      setPlans(Array.isArray(data) ? data : (data.items || []));
    } catch {
      // plans optional
    }
  };

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const query = {
        pageNumber: page,
        pageSize: pagination.pageSize || 20,
        sortBy: "createdAt",
        sortOrder: "DESC"
      };
      if (filters.search) query.search = filters.search;
      if (filters.status !== "") query.status = filters.status;
      if (filters.planId) query.planId = filters.planId;
      if (filters.overdue !== "") query.overdue = filters.overdue;
      if (filters.fiscalReady !== "") query.fiscalReady = filters.fiscalReady;

      const data = await listAdminCompanies(query);
      setItems(data.items || []);
      setPagination(data.pagination || {});
    } catch (err) {
      setError(err.message || "Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    load(1);
  };

  const clearFilters = () => {
    setFilters({ search: "", status: "", planId: "", overdue: "", fiscalReady: "" });
    setTimeout(() => load(1), 0);
  };

  const handleStatusToggle = async (e, company) => {
    e.stopPropagation();
    const newStatus = !company.status;
    if (!newStatus) {
      setConfirmModal({ open: true, company, reason: "", impact: null, loading: true });
      try {
        const impact = await getStatusImpact(company.id);
        setConfirmModal(prev => ({ ...prev, impact, loading: false }));
      } catch {
        setConfirmModal(prev => ({ ...prev, impact: null, loading: false }));
      }
    } else {
      setStatusLoading(company.id);
      try {
        await updateAdminCompanyStatus(company.id, { status: true, reason: "Ativação administrativa" });
        setToast({ type: "success", message: "Empresa ativada com sucesso" });
        load(pagination.pageNumber || 1);
      } catch (err) {
        setToast({ type: "error", message: err.message || "Erro ao ativar empresa" });
      } finally {
        setStatusLoading(null);
      }
    }
  };

  const confirmDeactivation = async () => {
    if (!confirmModal.reason.trim()) {
      setToast({ type: "error", message: "Motivo é obrigatório para inativação" });
      return;
    }
    setStatusLoading(confirmModal.company.id);
    try {
      await updateAdminCompanyStatus(confirmModal.company.id, { status: false, reason: confirmModal.reason });
      setToast({ type: "success", message: "Empresa inativada com sucesso" });
      setConfirmModal({ open: false, company: null, reason: "", impact: null, loading: false });
      load(pagination.pageNumber || 1);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Erro ao inativar empresa" });
    } finally {
      setStatusLoading(null);
    }
  };

  const getFiscalBadge = (item) => {
    if (!item.fiscalReadiness) {
      return <StatusBadge status="default" label="Não avaliado" />;
    }
    if (item.fiscalReadiness.ready) {
      return <StatusBadge status="ativo" label="Pronto" />;
    }
    return <StatusBadge status="pendente" label="Incompleto" />;
  };

  const columns = [
    { header: "ID", accessor: "id", render: (row) => <span className="font-mono text-purple-600 dark:text-purple-400">#{row.id}</span> },
    { header: "Empresa", accessor: "name", render: (row) => <span className="font-bold text-gray-900 dark:text-gray-100 block truncate max-w-[200px]" title={row.name}>{row.name}</span> },
    { header: "E-mail", accessor: "email", render: (row) => <span className="block truncate max-w-[200px]" title={row.email}>{row.email || "—"}</span> },
    { header: "Documento", accessor: "document", render: (row) => <span className="font-mono text-xs">{row.document || "—"}</span> },
    { header: "Plano", render: (row) => row.plan?.name || "—" },
    {
      header: "Status",
      render: (row) => <StatusBadge status={row.status ? "active" : "inactive"} label={row.status ? "Ativo" : "Inativo"} />
    },
    {
      header: "Fiscal",
      render: (row) => getFiscalBadge(row)
    },
    {
      header: "Vencimento",
      render: (row) => (row.dueDate ? new Date(row.dueDate).toLocaleDateString("pt-BR") : "—")
    },
    { header: "Usuários", render: (row) => <span className="text-center block">{row.users?.total ?? 0}</span> },
    { header: "Conexões", render: (row) => <span className="text-center block">{row.connections?.total ?? 0}</span> },
    {
      header: "Ações",
      render: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); history.push(`/companies/${row.id}`); }}
            className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors"
            title="Visualizar"
          >
            <Eye size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); history.push(`/companies/${row.id}/edit`); }}
            className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors"
            title="Editar"
          >
            <Edit size={15} />
          </button>
          <button
            onClick={(e) => handleStatusToggle(e, row)}
            disabled={statusLoading === row.id}
            className={`p-1 rounded-lg transition-colors ${
              row.status
                ? "bg-black/5 dark:bg-white/5 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400"
                : "bg-black/5 dark:bg-white/5 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
            }`}
            title={row.status ? "Inativar" : "Ativar"}
          >
            {row.status ? <PowerOff size={15} /> : <Power size={15} />}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Clientes & Acessos"
        title="Gestão de Empresas"
        subtitle={`${pagination.total || 0} empresa(s) cadastradas na plataforma`}
        action={
          <button
            onClick={() => history.push("/companies/new")}
            className="flex items-center gap-2 px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow"
          >
            <Plus size={14} /> Nova Empresa
          </button>
        }
      />

      {error && (
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <form onSubmit={handleSearchSubmit} className="flex gap-2.5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Buscar por empresa, e-mail ou documento..."
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <button type="submit" className="px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">
          Filtrar
        </button>
        <button
          type="button"
          onClick={clearFilters}
          className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl"
        >
          Limpar
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500"
        >
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>

        <select
          value={filters.planId}
          onChange={(e) => handleFilterChange("planId", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500"
        >
          <option value="">Todos os planos</option>
          {plans.map(plan => (
            <option key={plan.id} value={plan.id}>{plan.name}</option>
          ))}
        </select>

        <select
          value={filters.overdue}
          onChange={(e) => handleFilterChange("overdue", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500"
        >
          <option value="">Vencimento</option>
          <option value="true">Vencidos</option>
          <option value="false">Em dia</option>
        </select>

        <select
          value={filters.fiscalReady}
          onChange={(e) => handleFilterChange("fiscalReady", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500"
        >
          <option value="">Prontidão Fiscal</option>
          <option value="true">Pronto</option>
          <option value="false">Incompleto</option>
        </select>

        <button
          type="button"
          onClick={applyFilters}
          className="px-4 py-1.5 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow"
        >
          Aplicar
        </button>
      </div>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        emptyTitle="Nenhuma empresa encontrada"
        emptyDescription="Tente buscar por outro termo ou limpe os filtros."
      />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
          <span>Página {pagination.pageNumber} de {pagination.totalPages} ({pagination.total} empresas)</span>
          <div className="flex gap-2">
            <button
              disabled={pagination.pageNumber <= 1}
              onClick={() => load(pagination.pageNumber - 1)}
              className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Anterior
            </button>
            <button
              disabled={pagination.pageNumber >= pagination.totalPages}
              onClick={() => load(pagination.pageNumber + 1)}
              className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Confirmar Inativação</h3>
              <button onClick={() => setConfirmModal({ open: false, company: null, reason: "", impact: null, loading: false })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>

            {confirmModal.loading ? (
              <div className="py-6 text-center text-xs text-gray-400">Carregando impacto...</div>
            ) : (
              <>
                {confirmModal.impact && (
                  <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 space-y-2">
                    <p className="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2">
                      <AlertCircle size={14} /> Impacto da Inativação
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Users size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.activeUsers || 0} usuário(s) ativo(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Link2 size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.activeConnections || 0} conexão(ões) ativa(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <FileText size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.openTickets || 0} ticket(s) aberto(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Receipt size={12} className="text-purple-500" />
                        Assinatura: {confirmModal.impact.impact?.activeSubscription ? "Ativa" : "Inexistente"}
                      </div>
                      {confirmModal.impact.impact?.openInvoices > 0 && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 col-span-2">
                          <Receipt size={12} className="text-amber-500" />
                          {confirmModal.impact.impact.openInvoices} fatura(s) em aberto
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Informe o motivo da inativação da empresa <strong>{confirmModal.company?.name}</strong>:
                  </p>
                  <textarea
                    value={confirmModal.reason}
                    onChange={(e) => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500"
                    rows={3}
                    placeholder="Motivo da inativação..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
                  <button
                    onClick={() => setConfirmModal({ open: false, company: null, reason: "", impact: null, loading: false })}
                    className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmDeactivation}
                    disabled={!!statusLoading}
                    className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-xs font-bold text-white rounded-xl"
                  >
                    {statusLoading ? "Processando..." : "Confirmar Inativação"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
